import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CalendarPlus, FileText, Lock, Loader2, Paperclip } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { RichTextEditor } from "@/components/forms/RichTextEditor";
import api from "@/lib/api";
import { useEntitlement } from "@/context/AuthContext";
import { uploadFile } from "@/components/forms/MediaUploader";
import { validateUpload, limitHint } from "@/lib/uploadLimits";

const MESSAGE_MAX = 500;
// Count visible (plain-text) characters so HTML markup never counts toward the cap.
const PLAIN = (html) =>
  (html || "")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .trim().length;

const CONFIG = {
  personal_meeting: { ent: "personal.meeting.request", Icon: CalendarPlus, label: "Request a Meeting", attach: false },
  business_meeting: { ent: "business.meeting.request", Icon: CalendarPlus, label: "Request a Meeting", attach: false },
  business_pitchdeck: { ent: "business.pitchdeck.request", Icon: FileText, label: "Request a Pitch Deck", attach: false },
  capital_meeting: { ent: "capital.meeting.request", Icon: CalendarPlus, label: "Request a Meeting", attach: false },
  capital_pitchdeck: { ent: "capital.pitchdeck.submit", Icon: FileText, label: "Submit a Pitch Deck", attach: true },
};

export function InquiryButton({ type, targetId, targetName, variant = "secondary", primary = false }) {
  const cfg = CONFIG[type];
  const allowed = useEntitlement(cfg.ent);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const Icon = cfg.Icon;

  const submit = async () => {
    const plain = PLAIN(message);
    if (plain > MESSAGE_MAX) { toast.error(`Message must be ${MESSAGE_MAX} characters or fewer.`); return; }
    setBusy(true);
    try {
      await api.post("/inquiries", { type, target_id: targetId, message: plain > 0 ? message : "", attachment });
      toast.success("Sent! You'll be notified when they respond.");
      setOpen(false); setMessage(""); setAttachment(null);
    } catch (e) {
      toast.error((e && e.response && e.response.data && e.response.data.detail) || "Could not send");
    } finally { setBusy(false); }
  };

  const onFile = async (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    const chk = validateUpload(f, "pitchdeck");
    if (!chk.ok) { toast.error(chk.message); if (e.target) e.target.value = ""; return; }
    setBusy(true);
    try { const url = await uploadFile(f, "pitchdeck"); setAttachment({ url, name: f.name }); toast.success("Pitch deck attached"); }
    catch (_) { toast.error("Upload failed"); }
    finally { setBusy(false); if (e.target) e.target.value = ""; }
  };

  return (
    <>
      <Button variant={primary ? "default" : variant} className="w-full justify-start" onClick={() => setOpen(true)} data-testid={`inquiry-${type}`}>
        {allowed ? <Icon className="mr-2 h-4 w-4" /> : <Lock className="mr-2 h-4 w-4" />} {cfg.label}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          {allowed ? (
            <>
              <DialogHeader>
                <DialogTitle>{cfg.label}</DialogTitle>
                <DialogDescription>{targetName ? `Send to ${targetName}.` : "Send your request."}</DialogDescription>
              </DialogHeader>
              <div className="space-y-1.5">
                {(() => {
                  const plain = PLAIN(message);
                  return (
                    <div className="flex items-center justify-between">
                      <Label>Message <span className="font-[440] text-muted-foreground">(optional)</span></Label>
                      <span className={`text-xs tabular-nums ${plain > MESSAGE_MAX ? "font-[600] text-destructive" : "text-muted-foreground"}`} data-testid="inquiry-message-count">{plain}/{MESSAGE_MAX}</span>
                    </div>
                  );
                })()}
                <RichTextEditor
                  value={message}
                  onChange={setMessage}
                  toolbar={["bold", "italic", "underline", "link"]}
                  placeholder="Add a short note (optional)…"
                  testId="inquiry-message"
                />
              </div>
              {cfg.attach && (
                <div>
                  <input ref={fileRef} type="file" className="hidden" accept=".pdf,.ppt,.pptx,application/pdf" onChange={onFile} />
                  <Button type="button" variant="secondary" size="sm" onClick={() => fileRef.current && fileRef.current.click()} disabled={busy} data-testid="inquiry-attach">
                    <Paperclip className="mr-1.5 h-4 w-4" /> {attachment ? attachment.name : "Attach pitch deck"}
                  </Button>
                  <p className="mt-1 text-xs text-muted-foreground">PDF, PPT, or PPTX · {limitHint("pitchdeck")}</p>
                </div>
              )}
              <DialogFooter>
                <Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={submit} disabled={busy || PLAIN(message) > MESSAGE_MAX} data-testid="inquiry-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send"}</Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Upgrade to unlock</DialogTitle>
                <DialogDescription>{cfg.label} is available on a higher plan.</DialogDescription>
              </DialogHeader>
              <div className="flex flex-col items-center justify-center py-6 text-center">
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-teal-50"><Lock className="h-6 w-6 text-primary" /></div>
                <p className="mt-3 max-w-xs text-sm text-muted-foreground">Connect directly with founders and investors by upgrading your plan.</p>
              </div>
              <DialogFooter>
                <Button variant="secondary" onClick={() => setOpen(false)}>Not now</Button>
                <Link to="/pricing"><Button data-testid="inquiry-upgrade">View plans</Button></Link>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
